/**
 * HistoryAccordion Component
 * 본사 입출금 내역 아코디언 (협력사별/라이더별/외부 그룹화)
 */
import React, { useState, useMemo, useEffect } from 'react';
import { HeadWithdrawHistory, ExternalWithdrawalDto } from '../types/headWithdraw.types';
import { groupHistoriesByMode } from '../utils/groupHistories';
import HistoryAccordionItem from './HistoryAccordionItem';
import ExternalWithdrawTable from './ExternalWithdrawTable';
import '../styles/HistoryAccordion.css';
import '../styles/HistoryTable.css';

/** 페이지당 항목 수 */
const ITEMS_PER_PAGE = 15;

/** 뷰 모드: 협력사별, 라이더별, 외부 */
export type ViewMode = 'supplier' | 'rider' | 'external';

interface HistoryAccordionProps {
    /** 전체 내역 목록 */
    histories: HeadWithdrawHistory[];
    /** 뷰 모드 (협력사별 또는 라이더별 또는 외부) */
    viewMode: ViewMode;
    /** 뷰 모드 변경 핸들러 */
    onViewModeChange: (mode: ViewMode) => void;
    /** 협력사 사용자 여부 */
    isSupplierUser?: boolean;
    /** 협력사 사용자의 협력사 ID */
    userSuppId?: string;
    /** 라이더별 현재 잔액 맵 (백엔드에서 계산된 값) */
    currentBalanceByRider?: Record<string, number>;
    /** 협력사별 현재 잔액 맵 (백엔드에서 계산된 값) */
    currentBalanceBySupplier?: Record<string, number>;
    /** 외부 출금 내역 목록 */
    externalWithdrawals?: ExternalWithdrawalDto[];
    /** 외부 출금 합계 금액 */
    externalTotalAmount?: number;
}

const HistoryAccordion: React.FC<HistoryAccordionProps> = ({
    histories,
    viewMode,
    onViewModeChange,
    isSupplierUser = false,
    userSuppId: _userSuppId,
    currentBalanceByRider,
    currentBalanceBySupplier,
    externalWithdrawals = [],
    externalTotalAmount = 0,
}) => {
    const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
    const [groupPages, setGroupPages] = useState<Record<string, number>>({});
    const [hasAutoExpanded, setHasAutoExpanded] = useState(false);

    /**
     * viewMode에 따라 데이터 필터링
     * - 협력사 모드: reqType === 'S' (ACCT_TARGET='S')
     * - 라이더 모드: reqType === 'R' (ACCT_TARGET='R')
     */
    const filteredHistories = useMemo((): HeadWithdrawHistory[] => {
        const excludedTypes = ['SD', 'RD'];

        if (viewMode === 'supplier') {
            return histories.filter((h) => {
                if (excludedTypes.includes(h.acctType)) return false;
                return !h.riderId || h.riderId.trim() === '';
            });
        } else {
            return histories.filter(h => {
                if (excludedTypes.includes(h.acctType)) return false;
                return h.riderId && h.riderId.trim() !== '';
            });
        }
    }, [histories, viewMode]);

    /**
     * 데이터를 협력사 또는 라이더별로 그룹화
     */
    const groupedData = useMemo(() => {
        return groupHistoriesByMode(filteredHistories, viewMode, currentBalanceByRider, currentBalanceBySupplier);
    }, [filteredHistories, viewMode, currentBalanceByRider, currentBalanceBySupplier]);

    /**
     * 협력사 사용자일 때 협력사 모드에서 자동으로 아코디언 펼침
     */
    useEffect(() => {
        if (isSupplierUser && viewMode === 'supplier' && groupedData.length > 0 && !hasAutoExpanded) {
            const allIds = new Set(groupedData.map(group => group.id));
            setExpandedGroups(allIds);
            setHasAutoExpanded(true);
        }
    }, [isSupplierUser, viewMode, groupedData, hasAutoExpanded]);

    /**
     * 그룹 아코디언 토글
     */
    const toggleGroup = (groupId: string) => {
        setExpandedGroups(prev => {
            const newSet = new Set(prev);
            if (newSet.has(groupId)) {
                newSet.delete(groupId);
            } else {
                newSet.add(groupId);
            }
            return newSet;
        });
    };

    /**
     * 전체 펼치기
     */
    const expandAll = () => {
        const allIds = new Set(groupedData.map(group => group.id));
        setExpandedGroups(allIds);
    };

    /**
     * 전체 접기
     */
    const collapseAll = () => {
        setExpandedGroups(new Set());
    };

    /**
     * 전체 펼침 여부 확인
     */
    const isAllExpanded = groupedData.length > 0 && expandedGroups.size === groupedData.length;

    /**
     * 그룹별 페이지 변경
     */
    const handleGroupPageChange = (groupId: string, page: number) => {
        setGroupPages(prev => ({
            ...prev,
            [groupId]: page
        }));
    };

    /** 공통 뷰 모드 토글 버튼 */
    const renderToggle = () => (
        <div className="view-mode-toggle">
            <button
                className={`toggle-btn ${viewMode === 'supplier' ? 'active' : ''}`}
                onClick={() => onViewModeChange('supplier')}
            >
                <i className="fas fa-building" />
                협력사
            </button>
            <button
                className={`toggle-btn ${viewMode === 'rider' ? 'active' : ''}`}
                onClick={() => onViewModeChange('rider')}
            >
                <i className="fas fa-motorcycle" />
                라이더
            </button>
            <button
                className={`toggle-btn ${viewMode === 'external' ? 'active' : ''}`}
                onClick={() => onViewModeChange('external')}
            >
                <i className="fas fa-external-link-alt" />
                외부
            </button>
        </div>
    );

    /* ── 외부 모드 ── */
    if (viewMode === 'external') {
        return (
            <div className="card">
                <div className="card-body">
                    <div className="accordion-header-controls">
                        {renderToggle()}
                    </div>
                    <ExternalWithdrawTable
                        withdrawals={externalWithdrawals}
                        totalAmount={externalTotalAmount}
                    />
                </div>
            </div>
        );
    }

    /* ── 협력사/라이더 모드 — 데이터 없음 ── */
    if (histories.length === 0 || filteredHistories.length === 0) {
        return (
            <div className="card">
                <div className="card-body">
                    {renderToggle()}
                    <div className="empty-state">
                        <p>조회된 내역이 없습니다.</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="card">
            <div className="card-body">
                {/* 뷰 모드 토글 + 전체 펼치기/접기 */}
                <div className="accordion-header-controls">
                    {renderToggle()}
                    <button
                        className="accordion-toggle-btn"
                        onClick={isAllExpanded ? collapseAll : expandAll}
                    >
                        <i className={`fas fa-${isAllExpanded ? 'compress-alt' : 'expand-alt'}`}></i>
                        {isAllExpanded ? '전체 접기' : '전체 펼치기'}
                    </button>
                </div>

                {/* 아코디언 목록 */}
                <div className="history-accordion">
                    {groupedData.map((group) => (
                        <HistoryAccordionItem
                            key={group.id}
                            group={group}
                            isExpanded={expandedGroups.has(group.id)}
                            currentPage={groupPages[group.id] || 1}
                            onToggle={toggleGroup}
                            onPageChange={handleGroupPageChange}
                            viewMode={viewMode}
                            ITEMS_PER_PAGE={ITEMS_PER_PAGE}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
};

export default HistoryAccordion;
